import { create } from 'zustand';

/**
 * True when the app was opened as an OIDC-driven flow that must survive Firebase authenticating
 * mid-way: the IdP interaction UI (`/sso-login/:uid`), the shop autologin bridge (`/shop-bridge`,
 * which signs in with a custom token before redirecting on), or the silent cross-subdomain
 * auto-login iframe (`/silent-login`, which signs in with a custom token in place). Web-only;
 * native never routes here so it is always false.
 */
const isOidcInteractionUrl = (): boolean => {
  if (typeof window === 'undefined') return false;
  const path = window.location?.pathname ?? '';
  return (
    /^\/sso-login\/[^/]+/.test(path) || /^\/shop-bridge/.test(path) || /^\/silent-login/.test(path)
  );
};

type OidcInteractionStore = {
  /**
   * While true, the `withUnAuth` gate keeps rendering the UnAuth tree even after Firebase
   * flips the session to authenticated — so the SsoLogin screen stays mounted long enough
   * to hand the Firebase ID token to the IdP instead of being reset to the dashboard.
   */
  active: boolean;
  setActive: (active: boolean) => void;
};

export const useOidcInteractionStore = create<OidcInteractionStore>(set => ({
  // Seed from the URL so the gate is already correct on the very first render — including a
  // cold deep-link from an already-signed-in user (silent SSO), before SsoLogin mounts.
  active: isOidcInteractionUrl(),
  setActive: (active: boolean) => set({ active }),
}));
